/**
 * Reward Options Hooks
 *
 * Fetches available coupon offers and loyalty programs
 * for the current business, used in form builder reward settings.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface CouponOffer {
  id: string;
  name: string;
  description: string | null;
  discount_type: string;
  discount_value: number;
  is_active: boolean;
}

interface LoyaltyProgram {
  id: string;
  name: string;
  description: string | null;
  program_type: string;
  is_active: boolean;
}

export function useCouponOffers(businessId: string | undefined) {
  return useQuery({
    queryKey: ['coupon-offers', businessId],
    queryFn: async () => {
      if (!businessId) return [];

      const { data, error } = await supabase
        .from('coupon_offers')
        .select('id, name, description, discount_type, discount_value, is_active')
        .eq('business_id', businessId)
        .is('deleted_at', null)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as CouponOffer[];
    },
    enabled: !!businessId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useLoyaltyPrograms(businessId: string | undefined) {
  return useQuery({
    queryKey: ['loyalty-programs', businessId],
    queryFn: async () => {
      if (!businessId) return [];

      const { data, error } = await supabase
        .from('loyalty_programs')
        .select('id, name, description, program_type, is_active')
        .eq('business_id', businessId)
        .is('deleted_at', null)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as LoyaltyProgram[];
    },
    enabled: !!businessId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
